import { useContext, useEffect, useState } from 'react';
import moment from 'moment';

// Nucleus Components
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useLazyDataModel, extend } from '@thd-nucleus/data-sources';

// Local Components
import { GET_RENTAL_FDAV1_MODIFYING_QUERY, GET_RENTAL_FDAV4_QUERY } from '../utils/queries';
import { FeatureSwitchContext } from '../context/feature-switch-context';
import { ReservationContext } from '../context/reservation-context';
import { ReservationLookupApiContext } from '../context/ReservationLookupApiContext';
import { getFDAv4Availability, getModifyAvailability } from '../utils/api';
import helper from '../utils/helper';

export const useModifyAvailabilityData = () => {
  const [data, setData] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [isExecutable, setIsExecutable] = useState(true);

  const { FDAv4Enabled, graphQlEnabled } = useContext(FeatureSwitchContext);

  const [reservationState, _dispatch] = useContext(ReservationContext);
  const {
    reservation: {
      pickupDate,
      pickupTime,
      dropoffDate,
      dropoffTime
    } = {},
    nearbyWhitelistedStoresList
  } = reservationState;

  const { data: reservationData } = useContext(ReservationLookupApiContext);
  const {
    id: reservationId,
    store_number: storeId,
    tools: [
      {
        category_code: rentalCategory,
        subcategory_code: rentalSubCategory
      },
      ..._multiToolNotSupported
    ]
  } = reservationData;

  const ctx = useContext(ExperienceContext);
  const { hosts } = ctx;
  const { apionline } = hosts || {};

  const variablesFDAv1 = {
    category: rentalCategory,
    subCategory: rentalSubCategory,
    locationNbr: storeId,
    reservationId,
    startTime: moment(pickupDate, 'MM/DD/YYYY').format('YYYY-MM-DD'),
    endTime: moment(dropoffDate, 'MM/DD/YYYY').format('YYYY-MM-DD')
  };

  const variablesFDAv4 = {
    appFlow: 'reservation',
    storeList: [storeId],
    tools: [
      {
        categoryCode: rentalCategory,
        subCategoryCode: rentalSubCategory,
        pickupTime: moment(pickupDate, 'MM/DD/YYYY').format('YYYY-MM-DD'),
        returnTime: moment(dropoffDate, 'MM/DD/YYYY').format('YYYY-MM-DD'),
        count: 1,
      }
    ],
    reservationId
  };

  const [getRentalFdaV1Modifying] = useLazyDataModel('getRentalFdaV1Modifying', {
    variablesFDAv1,
    ssr: false,
  });

  const [getRentalFDAv4] = useLazyDataModel('getRentalFdaV4', {
    variablesFDAv4,
    ssr: false,
  });

  const getRentalFdaV1ModifyingData = async () => {
    if (graphQlEnabled) {
      const fdav1GraphQlData = await helper.graphQlHandler(getRentalFdaV1Modifying, variablesFDAv1);
      return fdav1GraphQlData?.getRentalFdaV1Modifying;
    }

    const fdav1ApiData = await getModifyAvailability({
      host: apionline,
      storeId,
      reservationId,
      rentalCategory,
      rentalSubCategory,
      startDate: pickupDate,
      startTime: pickupTime,
      endDate: dropoffDate,
      endTime: dropoffTime,
    });

    return fdav1ApiData;
  };

  const getFDAv4Data = async () => {
    if (graphQlEnabled) {
      const fdav4GraphQlData = await helper.graphQlHandler(getRentalFDAv4, variablesFDAv4);
      return fdav4GraphQlData?.getRentalFdaV4;
    }

    const fdav4ApiData = await getFDAv4Availability({
      host: apionline,
      storeList: [storeId],
      reservationId,
      rentalCategory,
      rentalSubCategory,
      startDate: pickupDate,
      startTime: pickupTime,
      endDate: dropoffDate,
      endTime: dropoffTime,
    });

    return fdav4ApiData;
  };

  const executeRequest = async () => {
    if (isExecutable) {
      setLoading(true);
      setIsExecutable(false);
      setError('');
      try {
        let result;
        if (FDAv4Enabled) {
          result = await getFDAv4Data();
        } else {
          result = await getRentalFdaV1ModifyingData();
        }

        const remappedAvailability = helper.remapAvailabilityData(
          helper.formatAvailability({
            stores: nearbyWhitelistedStoresList,
            availability: result
          }),
          reservationState
        );
        setData(remappedAvailability);
      } catch (err) {
        setError(err.message || 'Unexpected Error');
        setData([]);
        setIsExecutable(true);
      } finally {
        setLoading(false);
      }
    } else {
      setData([...data]);
    }
  };

  useEffect(() => {
    if (!isExecutable) {
      setIsExecutable(true);
      setData([]);
      setError('');
    }
  }, [
    reservationData,
    pickupDate,
    pickupTime,
    dropoffDate,
    dropoffTime
  ]);

  return {
    data,
    error,
    loading,
    executeRequest
  };
};

useModifyAvailabilityData.dataModel = extend(GET_RENTAL_FDAV1_MODIFYING_QUERY, GET_RENTAL_FDAV4_QUERY);